import { useState, useEffect, useMemo } from "react";
import api from "../../services/api";
import { Plus, Search, Edit2, ShieldAlert, ShieldCheck, Building2, Globe, TrendingUp, Calendar, AlertCircle } from "lucide-react";
import toast, { Toaster } from "react-hot-toast";

function KPICard({ label, value, icon: Icon, color }) {
  return (
    <div className="bg-white dark:bg-slate-900 rounded-2xl p-5 shadow-sm border border-slate-200 dark:border-slate-800 flex items-center justify-between">
      <div>
        <p className="text-xs font-bold uppercase tracking-widest text-slate-500">{label}</p>
        <p className="text-2xl font-black text-slate-900 dark:text-white mt-1">{value}</p>
      </div>
      <div className={`p-3 rounded-xl ${color}`}>
        <Icon size={20} className="text-white" />
      </div>
    </div>
  );
}

export default function AdminInstitutes() {
  const [institutes, setInstitutes] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filter, setFilter] = useState("All");
  
  const [showModal, setShowModal] = useState(false);
  const [editingInstitute, setEditingInstitute] = useState(null);
  const [plans, setPlans] = useState([]);
  
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    password: "",
    mobile_no: "",
    subdomain: "",
    role: "institute",
    plan_id: ""
  });

  const fetchData = async () => {
    try {
      setLoading(true);
      const [usersRes, statsRes, plansRes] = await Promise.all([
        api.get("/admin/users"),
        api.get("/admin/stats"),
        api.get("/saas/plans")
      ]);
      setInstitutes(usersRes.data.filter(u => u.role === "institute"));
      setStats(statsRes.data);
      setPlans(plansRes.data || []);
    } catch (err) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const openModal = (inst = null) => {
    if (inst) {
      setEditingInstitute(inst);
      setForm({
        full_name: inst.full_name || "",
        email: inst.email || "",
        password: "",
        mobile_no: inst.mobile_no || "",
        subdomain: inst.subdomain || "",
        role: "institute",
        plan_id: ""
      });
    } else {
      setEditingInstitute(null);
      setForm({
        full_name: "",
        email: "",
        password: "",
        mobile_no: "",
        subdomain: "",
        role: "institute",
        plan_id: plans.length > 0 ? plans[0].id : ""
      });
    }
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingInstitute) {
        await api.put(`/admin/users/${editingInstitute.id}`, {
          role: "institute",
          full_name: form.full_name,
          mobile_no: form.mobile_no,
          subdomain: form.subdomain
        });
        toast.success("Institute updated!");
      } else {
        await api.post("/admin/users", {
          ...form,
          plan_id: form.plan_id ? parseInt(form.plan_id) : null
        });
        toast.success("Institute created!");
      }
      setShowModal(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Action failed");
    }
  };

  const handleToggleActive = async (userId) => {
    try {
      const res = await api.post(`/admin/users/${userId}/toggle-active`);
      toast.success(res.data.msg || "Status toggled");
      fetchData();
    } catch (err) {
      toast.error("Action failed");
    }
  };

  const handleGenerateApp = async (instId, appIconUrl) => {
    try {
      await api.post(`/admin/generate-app/${instId}`, { app_icon: appIconUrl });
      toast.success("App generation started!");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to start app build");
    }
  };

  // Poll for build progress
  useEffect(() => {
    const buildingInstitutes = institutes.filter(inst => inst.app_build_status === 'queued' || inst.app_build_status === 'building');
    if (buildingInstitutes.length === 0) return;

    const interval = setInterval(() => {
      fetchData(); // Simplest way to refresh progress
    }, 5000);
    return () => clearInterval(interval);
  }, [institutes]);

  const filteredInstitutes = useMemo(() => {
    let result = institutes;
    
    if (searchTerm) {
      const lower = searchTerm.toLowerCase();
      result = result.filter(inst => 
        inst.full_name?.toLowerCase().includes(lower) ||
        inst.email?.toLowerCase().includes(lower) ||
        inst.subdomain?.toLowerCase().includes(lower)
      );
    }

    const now = new Date();
    
    switch (filter) {
      case "Trial Active":
        result = result.filter(inst => inst.is_trial);
        break;
      case "Trial Expired":
        result = result.filter(inst => !inst.is_trial && !inst.is_subscription_active && inst.subscription_status === "expired");
        break;
      case "Subscription Active":
        result = result.filter(inst => inst.is_subscription_active);
        break;
      case "Subscription Expired":
        result = result.filter(inst => !inst.is_subscription_active && !inst.is_trial && inst.subscription_status === "expired");
        break;
      case "Expiring 7 Days":
        result = result.filter(inst => {
          if (!inst.is_subscription_active || !inst.subscription_end_date) return false;
          const diff = (new Date(inst.subscription_end_date) - now) / (1000*60*60*24);
          return diff >= 0 && diff <= 7;
        });
        break;
      case "Expiring 30 Days":
        result = result.filter(inst => {
          if (!inst.is_subscription_active || !inst.subscription_end_date) return false;
          const diff = (new Date(inst.subscription_end_date) - now) / (1000*60*60*24);
          return diff >= 0 && diff <= 30;
        });
        break;
      default:
        break;
    }
    
    return result;
  }, [institutes, searchTerm, filter]);

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <Toaster position="top-right" />
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-extrabold text-slate-900 dark:text-white flex items-center gap-3">
            <Building2 size={24} className="text-indigo-600 dark:text-indigo-400" />
            Manage SaaS Institutes
          </h1>
          <p className="text-slate-500 mt-1">View, suspend, and manage all tenant institutes and subscriptions.</p>
        </div>
        <button onClick={() => openModal()} className="btn-primary">
          <Plus size={18} /> Add Institute
        </button>
      </div>

      {/* KPI Cards */}
      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
          <KPICard label="Total Inst." value={stats.total_institutes} icon={Building2} color="bg-blue-500" />
          <KPICard label="Trial Inst." value={stats.trial_institutes} icon={Calendar} color="bg-amber-500" />
          <KPICard label="Active Subs" value={stats.active_saas_subscriptions} icon={ShieldCheck} color="bg-emerald-500" />
          <KPICard label="Expired Trials" value={stats.expired_trials} icon={AlertCircle} color="bg-rose-500" />
          <KPICard label="Renewal Due" value={stats.renewal_due} icon={Calendar} color="bg-orange-500" />
          <KPICard label="Monthly Rev." value={`₹${stats.total_revenue}`} icon={TrendingUp} color="bg-indigo-500" />
        </div>
      )}

      {/* Filters and Search */}
      <div className="flex flex-col sm:flex-row items-center gap-4 bg-white p-4 rounded-xl shadow-sm border border-slate-200">
        <div className="relative w-full sm:w-80">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
          <input
            type="text"
            placeholder="Search institutes..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full rounded-lg border border-slate-200 bg-slate-50 py-2 pl-10 pr-4 text-sm focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20"
          />
        </div>
        <select 
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="w-full sm:w-auto rounded-lg border border-slate-200 bg-slate-50 py-2 px-4 text-sm font-semibold focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20"
        >
          <option value="All">All Institutes</option>
          <option value="Trial Active">Trial Active</option>
          <option value="Trial Expired">Trial Expired</option>
          <option value="Subscription Active">Subscription Active</option>
          <option value="Subscription Expired">Subscription Expired</option>
          <option value="Expiring 7 Days">Expiring Within 7 Days</option>
          <option value="Expiring 30 Days">Expiring Within 30 Days</option>
        </select>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm text-slate-600 whitespace-nowrap">
            <thead className="border-b border-slate-200 bg-slate-50 text-xs font-bold uppercase text-slate-500">
              <tr>
                <th className="px-6 py-4">Institute</th>
                <th className="px-6 py-4">Plan</th>
                <th className="px-6 py-4">Trial</th>
                <th className="px-6 py-4">Subscription</th>
                <th className="px-6 py-4">Days Left</th>
                <th className="px-6 py-4">Status</th>
                <th className="px-6 py-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                <tr>
                  <td colSpan="7" className="py-12 text-center text-slate-500">Loading institutes...</td>
                </tr>
              ) : filteredInstitutes.length === 0 ? (
                <tr>
                  <td colSpan="7" className="py-12 text-center text-slate-500">No institutes found matching criteria.</td>
                </tr>
              ) : (
                filteredInstitutes.map((inst) => {
                  let daysLeft = "-";
                  const now = new Date();
                  if (inst.is_trial && inst.trial_end_date) {
                    daysLeft = Math.max(0, Math.ceil((new Date(inst.trial_end_date) - now) / (1000*60*60*24)));
                  } else if (inst.is_subscription_active && inst.subscription_end_date) {
                    daysLeft = Math.max(0, Math.ceil((new Date(inst.subscription_end_date) - now) / (1000*60*60*24)));
                  }

                  let derivedStatus = "Active";
                  if (inst.is_trial) derivedStatus = "Trial Active";
                  else if (inst.is_subscription_active) derivedStatus = "Subscription Active";
                  else if (inst.subscription_status === "expired") derivedStatus = "Expired";

                  return (
                  <tr key={inst.id} className="transition-colors hover:bg-slate-50">
                    <td className="px-6 py-4">
                      <p className="font-bold text-slate-900">{inst.full_name}</p>
                      <p className="text-[11px] text-slate-500 font-mono mt-0.5">{inst.mobile_no || "No mobile"}</p>
                      <p className="text-xs text-indigo-600 font-medium mt-0.5">{inst.subdomain ? `${inst.subdomain}.skillzy.pro` : 'No domain'}</p>
                    </td>
                    <td className="px-6 py-4">
                      <p className="font-semibold text-slate-700">{inst.plan || 'Unknown'}</p>
                      {inst.plan_price !== undefined && inst.plan_price !== null && (
                        <p className="text-[11px] text-slate-500 font-bold mt-0.5">
                          {inst.plan_price === 0 ? "Free" : `₹${inst.plan_price.toLocaleString()} / ${inst.plan_billing_cycle === 'yearly' ? 'yr' : 'mo'}`}
                        </p>
                      )}
                    </td>
                    <td className="px-6 py-4 text-xs">
                      {inst.trial_start_date ? (
                        <>
                          <p><span className="font-bold text-slate-400">Start:</span> {new Date(inst.trial_start_date).toLocaleDateString()}</p>
                          <p><span className="font-bold text-slate-400">End:</span> {new Date(inst.trial_end_date).toLocaleDateString()}</p>
                        </>
                      ) : "-"}
                    </td>
                    <td className="px-6 py-4 text-xs">
                      {inst.subscription_start_date ? (
                        <>
                          <p><span className="font-bold text-slate-400">Start:</span> {new Date(inst.subscription_start_date).toLocaleDateString()}</p>
                          <p><span className="font-bold text-slate-400">End:</span> {new Date(inst.subscription_end_date).toLocaleDateString()}</p>
                        </>
                      ) : "-"}
                    </td>
                    <td className="px-6 py-4 font-bold">
                      {daysLeft} {daysLeft !== "-" && "Days"}
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center rounded-md px-2 py-1 text-xs font-bold border ${
                        derivedStatus.includes("Active") 
                          ? "bg-emerald-50 text-emerald-700 border-emerald-200" 
                          : "bg-rose-50 text-rose-700 border-rose-200"
                      }`}>
                        {derivedStatus}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      {inst.app_build_status === 'completed' && inst.app_download_url ? (
                        <a href={inst.app_download_url} target="_blank" rel="noreferrer" className="text-xs font-bold text-indigo-600 hover:underline">
                          Download APK
                        </a>
                      ) : inst.app_build_status === 'queued' || inst.app_build_status === 'building' ? (
                        <div className="flex flex-col gap-1">
                          <span className="text-[10px] font-bold text-indigo-500 uppercase">{inst.app_build_status}... {inst.app_build_progress}%</span>
                          <div className="w-24 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                            <div className="h-full bg-indigo-500 transition-all duration-500" style={{ width: `${inst.app_build_progress}%` }}></div>
                          </div>
                        </div>
                      ) : (
                        <span className="text-[10px] font-bold text-slate-400">No App</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => {
                            const iconUrl = prompt("Enter an image URL for the App Icon (Leave blank for default):");
                            if (iconUrl !== null) handleGenerateApp(inst.id, iconUrl);
                          }}
                          title="Generate White-Label App"
                          className="p-1.5 rounded-lg text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 transition-colors"
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 19H5a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v5.5"/><path d="M16 3v4"/><path d="M8 3v4"/><path d="M3 11h18"/><path d="M19 16v6"/><path d="M22 19l-3 3-3-3"/></svg>
                        </button>
                        <button onClick={() => openModal(inst)} className="p-1.5 rounded-lg text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 transition-colors">
                          <Edit2 size={16} />
                        </button>
                        <button
                          onClick={() => handleToggleActive(inst.id)}
                          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                            inst.is_active !== false 
                            ? "bg-rose-50 text-rose-600 hover:bg-rose-100" 
                            : "bg-emerald-50 text-emerald-600 hover:bg-emerald-100"
                          }`}
                        >
                          {inst.is_active !== false ? <><ShieldAlert size={14}/> Suspend</> : <><ShieldCheck size={14}/> Activate</>}
                        </button>
                      </div>
                    </td>
                  </tr>
                )}))}
            </tbody>
          </table>
        </div>
      </div>

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4 backdrop-blur-sm">
          <div className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-xl dark:bg-slate-900 border dark:border-slate-800">
            <h2 className="mb-4 text-xl font-bold text-slate-900 dark:text-white">
              {editingInstitute ? "Edit Institute" : "Add New Institute"}
            </h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="mb-1 block text-sm font-semibold text-slate-700 dark:text-slate-300">Institute Name</label>
                <input
                  type="text"
                  required
                  className="w-full rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm dark:border-slate-800 dark:bg-slate-950 dark:text-white"
                  value={form.full_name}
                  onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="mb-1 block text-sm font-semibold text-slate-700 dark:text-slate-300">Subdomain</label>
                  <input
                    type="text"
                    className="w-full rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm dark:border-slate-800 dark:bg-slate-950 dark:text-white"
                    value={form.subdomain}
                    onChange={(e) => setForm({ ...form, subdomain: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })}
                    placeholder="e.g. myinstitute"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-semibold text-slate-700 dark:text-slate-300">Mobile No</label>
                  <input
                    type="text"
                    className="w-full rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm dark:border-slate-800 dark:bg-slate-950 dark:text-white"
                    value={form.mobile_no}
                    onChange={(e) => setForm({ ...form, mobile_no: e.target.value })}
                  />
                </div>
              </div>
              {!editingInstitute && (
                <>
                  <div>
                    <label className="mb-1 block text-sm font-semibold text-slate-700 dark:text-slate-300">Email Address (Login ID)</label>
                    <input
                      type="email"
                      required
                      className="w-full rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm dark:border-slate-800 dark:bg-slate-950 dark:text-white"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="mb-1 block text-sm font-semibold text-slate-700 dark:text-slate-300">Password</label>
                      <input
                        type="password"
                        required
                        className="w-full rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm dark:border-slate-800 dark:bg-slate-950 dark:text-white"
                        value={form.password}
                        onChange={(e) => setForm({ ...form, password: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-sm font-semibold text-slate-700 dark:text-slate-300">SaaS Plan</label>
                      <select
                        className="w-full rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm dark:border-slate-800 dark:bg-slate-950 dark:text-slate-800 font-semibold"
                        value={form.plan_id}
                        onChange={(e) => setForm({ ...form, plan_id: e.target.value })}
                      >
                        {plans.map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.name} (₹{p.price})
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </>
              )}
              <div className="mt-6 flex gap-3">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 rounded-xl bg-slate-100 py-3 text-sm font-bold text-slate-600 transition-colors hover:bg-slate-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 rounded-xl bg-indigo-600 py-3 text-sm font-bold text-white transition-colors hover:bg-indigo-700"
                >
                  {editingInstitute ? "Save Changes" : "Create Institute"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
